import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BrandHome } from "@/components/BrandHome";
import { ArrowRight, ArrowLeft, Loader2, Calendar, Clock, Tag, Rocket, BookOpen, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import Seo, { SITE_URL } from "@/components/Seo";
import { Helmet } from "react-helmet-async";
import SiteFooter from "@/components/SiteFooter";
import ScrollToTop from "@/components/ScrollToTop";

const TYPE_LABEL = { article: "مقال", success_story: "قصة نجاح" };

function readingMinutes(content) {
  const words = (content || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 180));
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

function BlogChrome({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <header className="glass border-b border-border sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandHome />
          <div className="flex items-center gap-3 text-sm">
            <Link to="/guide" className="text-muted-foreground hover:text-foreground transition" data-testid="blog-nav-guide">دليل الاستخدام</Link>
            <Link to="/blog" className="text-muted-foreground hover:text-foreground transition" data-testid="blog-nav-link">المدونة</Link>
            <Link to="/register"><Button size="sm" className="bg-primary hover:bg-primary/90 text-white">ابدأ الآن</Button></Link>
          </div>
        </div>
      </header>
      {children}
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}

function PostCard({ p }) {
  return (
    <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
      className="group glass rounded-2xl border border-border overflow-hidden flex flex-col hover:border-primary/40 transition-all">
      <div className="aspect-[16/10] overflow-hidden bg-secondary">
        {p.cover_image && <img src={p.cover_image} alt={p.title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold ${p.type === "success_story" ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>
            {TYPE_LABEL[p.type] || "مقال"}
          </span>
          <span className="text-[11px] text-muted-foreground">{fmtDate(p.created_at)}</span>
        </div>
        <h3 className="font-bold text-base leading-snug mb-2 group-hover:text-primary transition">{p.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
        <span className="mt-4 text-sm text-primary font-semibold flex items-center gap-1">
          اقرأ المزيد <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export function BlogList() {
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/blog/posts").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);

  const filtered = (posts || []).filter((p) => filter === "all" || p.type === filter);
  const tabs = [
    { id: "all", label: "الكل" },
    { id: "article", label: "مقالات" },
    { id: "success_story", label: "قصص نجاح" },
  ];

  return (
    <BlogChrome>
      <Seo
        title="المدونة — مقالات التسويق وقصص النجاح"
        description="مقالات ودلائل عملية في التسويق الرقمي والذكاء الاصطناعي للسوق السعودي، وقصص نجاح حقيقية تُظهر كيف تعمل منصة رواج الذكية وأثرها على العملاء."
        keywords="مدونة تسويق, التسويق بالذكاء الاصطناعي, توليد الصور بالذكاء الاصطناعي, توليد الفيديو بالذكاء الاصطناعي, صناعة استراتيجية تسويق, الحلول الرقمية, حلول تسويق, قصص نجاح, تسويق رقمي السعودية"
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "مدونة رواج الذكية",
          url: `${SITE_URL}/blog`,
          description: "مقالات التسويق الرقمي وقصص النجاح من منصة رواج الذكية.",
        }}
      />
      <section className="max-w-5xl mx-auto px-6 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 text-xs text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
          <BookOpen className="w-3.5 h-3.5" /> مدونة رواج الذكية
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-heading mb-4">رؤى التسويق وقصص النجاح</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          دلائل عملية في التسويق الرقمي والذكاء الاصطناعي للسوق السعودي، وقصص نجاح واقعية تُظهر كيف تتحوّل الفكرة إلى حضور تسويقي مؤثر.
        </p>
      </section>

      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="blog-filters">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`blog-filter-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${filter === t.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {posts === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-20" data-testid="blog-empty">لا توجد مقالات بعد.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {filtered.map((p) => <PostCard key={p.slug} p={p} />)}
          </div>
        )}
      </div>
    </BlogChrome>
  );
}

function renderContent(content) {
  const blocks = (content || "").split(/\n\n+/);
  return blocks.map((b, i) => {
    const t = b.trim();
    if (!t) return null;
    if (t.startsWith("## ")) return <h2 key={i} className="text-2xl font-bold mt-9 mb-3 text-foreground">{t.slice(3)}</h2>;
    const lines = t.split("\n");
    if (lines.every((l) => l.trim().startsWith("- "))) {
      return <ul key={i} className="list-disc pr-6 space-y-2 my-4 text-foreground/85 leading-relaxed">{lines.map((l, j) => <li key={j}>{l.replace(/^-\s*/, "")}</li>)}</ul>;
    }
    return <p key={i} className="leading-loose my-4 text-foreground/85 text-[15px]">{t}</p>;
  });
}

export function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/blog/posts/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <BlogChrome>
      <Helmet><title>مدونة رواج الذكية</title><meta name="robots" content="noindex, follow" /><link rel="canonical" href={`${SITE_URL}/blog`} /><meta httpEquiv="refresh" content="3; url=/blog" /></Helmet>
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="blog-notfound">
        <h1 className="text-2xl font-bold mb-3">مدونة رواج الذكية</h1>
        <p className="text-muted-foreground mb-4">هذا المقال لم يعد متاحًا — سننقلك إلى المدونة خلال ثوانٍ.</p>
        <Link to="/blog" className="text-primary hover:underline">العودة إلى المدونة الآن</Link>
      </div>
    </BlogChrome>
  );

  if (!data) return (
    <BlogChrome><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></BlogChrome>
  );

  const p = data.post;
  const isStory = p.type === "success_story";

  return (
    <BlogChrome>
      <Seo
        title={p.title}
        description={p.meta_description || p.excerpt}
        keywords={(p.tags || []).map((t) => t.replace(/^#/, "").replace(/_/g, " ")).join(", ")}
        image={p.cover_image}
        path={`/blog/${p.slug}`}
        type="article"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": isStory ? "Article" : "BlogPosting",
          headline: p.title,
          description: p.meta_description || p.excerpt,
          image: p.cover_image,
          datePublished: p.created_at,
          dateModified: p.updated_at,
          author: { "@type": "Organization", name: p.author || "رواج الذكية" },
          publisher: { "@type": "Organization", name: "رواج الذكية", logo: { "@type": "ImageObject", url: `${SITE_URL}/rawaj-logo.png` } },
          mainEntityOfPage: `${SITE_URL}/blog/${p.slug}`,
          keywords: (p.tags || []).join(", "),
          inLanguage: "ar",
          about: "التسويق بالذكاء الاصطناعي في السعودية والخليج والدول العربية",
        }, ...(p.faq?.length ? [{
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }] : [])]}
      />

      <article className="max-w-3xl mx-auto px-6 pt-10">
        <Link to="/blog" className="text-sm text-primary flex items-center gap-1 hover:underline mb-6" data-testid="blog-back-link">
          <ArrowRight className="w-4 h-4" /> كل المقالات
        </Link>

        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${isStory ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>
          {TYPE_LABEL[p.type] || "مقال"}
        </span>
        <h1 className="text-3xl sm:text-4xl font-extrabold font-heading mt-4 mb-4 leading-tight">{p.title}</h1>

        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap mb-6">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(p.created_at)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {readingMinutes(p.content)} دقائق قراءة</span>
          <span>بقلم {p.author || "فريق رواج الذكية"}</span>
          {isStory && p.client_name && <span className="flex items-center gap-1 text-accent font-semibold">{p.client_name}</span>}
        </div>

        {p.cover_image && (
          <img src={p.cover_image} alt={p.title} className="w-full rounded-2xl border border-border mb-8 object-cover" data-testid="blog-cover" />
        )}

        {isStory && (p.results || []).length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="blog-results">
            {p.results.map((r, i) => (
              <div key={i} className="glass rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-extrabold text-accent flex items-center justify-center gap-1"><TrendingUp className="w-4 h-4" /> {r.value}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{r.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="prose-rtl" data-testid="blog-content">{renderContent(p.content)}</div>

        {(p.media || []).length > 0 && (
          <div className="mt-10 space-y-6" data-testid="blog-media">
            {p.media.map((m, i) => (
              <figure key={i} className="rounded-2xl overflow-hidden border border-border">
                {m.type === "video" ? (
                  <video src={m.url} controls playsInline className="w-full bg-black" />
                ) : (
                  <img src={m.url} alt={m.caption || ""} loading="lazy" className="w-full object-cover" />
                )}
                {m.caption && <figcaption className="text-xs text-muted-foreground text-center py-2 bg-secondary">{m.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}

        {(p.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8" data-testid="blog-tags">
            {p.tags.map((t, i) => (
              <span key={i} className="text-xs text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1 flex items-center gap-1">
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
        )}

        <div className="glass rounded-2xl border border-primary/30 bg-primary/5 p-6 mt-12 text-center">
          <h3 className="text-xl font-bold mb-2">جاهز تطبّق هذا على مشروعك؟</h3>
          <p className="text-sm text-muted-foreground mb-4">دع رواج الذكية يبني استراتيجيتك ويولّد محتواك التسويقي بالذكاء الاصطناعي.</p>
          <Link to="/register"><Button className="bg-primary hover:bg-primary/90 text-white font-bold gap-2"><Rocket className="w-4 h-4" /> ابدأ الآن مجاناً</Button></Link>
        </div>
      </article>

      {(data.related || []).length > 0 && (
        <section className="max-w-5xl mx-auto px-6 mt-16">
          <h2 className="text-xl font-bold mb-6">مقالات ذات صلة</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.related.map((r) => <PostCard key={r.slug} p={r} />)}
          </div>
        </section>
      )}
    </BlogChrome>
  );
}
